/**
 * AdminTheme — let the tenant pick a storefront theme + fonts.
 *
 * - 4 ready-made presets (Bizil Green, Swiggy Orange, Zomato Red, Midnight Dark)
 * - Body + heading font from 4 curated Google Fonts
 * - Live preview card on the right reflects the choice instantly
 * - Save POSTs to `PATCH /api/admin/branding` which already accepts arbitrary
 *   `branding.*` keys; TenantContext re-applies the theme on next page load.
 */
import { useEffect, useState } from "react";
import { Loader2, Check, Sparkles, X } from "lucide-react";
import { api, resolveTenantSlug } from "../../lib/api";
import { toast } from "sonner";
import { THEME_PRESETS, FONT_CHOICES, applyThemePreset, hexToHslTriple } from "../../lib/themes";

export default function AdminTheme() {
    const [presetId, setPresetId] = useState("bizil-green");
    const [bodyFont, setBodyFont] = useState("Inter");
    const [headingFont, setHeadingFont] = useState("Inter");
    const [customHex, setCustomHex] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/tenant/by-slug/" + resolveTenantSlug())
            .then(({ data }) => {
                const b = data?.branding || {};
                setPresetId(b.theme_preset || "bizil-green");
                setBodyFont(b.body_font || "Inter");
                setHeadingFont(b.heading_font || b.body_font || "Inter");
                setCustomHex(b.custom_primary_hex || "");
            })
            .finally(() => setLoaded(true));
    }, []);

    // Live preview — apply to a local element only (not the whole page) so the
    // admin can experiment without nuking the dashboard theme.
    const preset = THEME_PRESETS[presetId];
    const overrideTriple = customHex ? hexToHslTriple(customHex) : null;
    const previewTokens = {
        ...(preset?.tokens || {}),
        ...(overrideTriple ? { "--primary": overrideTriple } : {}),
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/admin/branding", {
                theme_preset: presetId,
                theme_mode: preset.mode,
                body_font: bodyFont,
                heading_font: headingFont,
                custom_primary_hex: customHex || null,
            });
            toast.success("Theme saved — refresh the storefront to see it live");
            applyThemePreset(presetId, {
                body_font: bodyFont,
                heading_font: headingFont,
                custom_primary_hex: customHex || null,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    if (!loaded) {
        return <div className="p-6 flex items-center justify-center"><Loader2 size={20} className="animate-spin" /></div>;
    }

    return (
        <div data-testid="admin-theme">
            <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Sparkles size={20} className="text-amber-500" /> Storefront Theme</h1>
            <p className="text-sm text-muted-foreground mt-1">Pick the look that customers see on the home page, category pages, restaurant pages, cart and checkout. Admin & merchant dashboards stay on the neutral theme.</p>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="md:col-span-2 space-y-6">
                    <section>
                        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Choose a preset</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {Object.entries(THEME_PRESETS).map(([id, p]) => {
                                const active = id === presetId;
                                return (
                                    <button
                                        key={id}
                                        data-testid={`theme-preset-${id}`}
                                        onClick={() => setPresetId(id)}
                                        className={`relative text-left bg-card border-2 rounded-2xl p-4 transition ${active ? "border-primary shadow-md" : "border-border hover:border-muted-foreground/30"}`}
                                    >
                                        {active && <Check size={16} className="absolute top-3 right-3 text-primary" />}
                                        <div className="flex gap-1.5 mb-3">
                                            {p.swatch.map((c, i) => (
                                                <span key={i} className="w-7 h-7 rounded-full border border-black/5" style={{ background: c }} />
                                            ))}
                                        </div>
                                        <p className="font-bold text-sm">{p.label}</p>
                                        <p className="text-[11px] text-muted-foreground mt-0.5 uppercase tracking-wider">{p.mode} mode</p>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section className="bg-card border border-border rounded-2xl p-4">
                        <h2 className="text-sm font-bold inline-flex items-center gap-2">Brand colour override <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">optional</span></h2>
                        <p className="text-xs text-muted-foreground mt-1">Keep the preset's layout but nudge the primary colour to match your brand. Leave empty to use the preset's own colour.</p>
                        <div className="mt-3 flex items-center gap-3">
                            <input
                                data-testid="theme-custom-hex"
                                type="color"
                                value={customHex || `#${(preset.tokens["--primary"] || "10B981").split(" ").length === 3 ? "10B981" : "10B981"}`}
                                onChange={(e) => setCustomHex(e.target.value)}
                                className="w-14 h-12 rounded-lg bg-muted cursor-pointer"
                            />
                            <input
                                data-testid="theme-custom-hex-text"
                                value={customHex}
                                onChange={(e) => setCustomHex(e.target.value)}
                                placeholder="#7C5CFF"
                                className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm font-mono"
                            />
                            {customHex && (
                                <button data-testid="theme-clear-hex" onClick={() => setCustomHex("")} className="p-2 rounded-lg hover:bg-muted text-muted-foreground" title="Clear override"><X size={16} /></button>
                            )}
                        </div>
                    </section>

                    <section>
                        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Typography</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <FontPicker label="Body font" value={bodyFont} onChange={setBodyFont} testid="font-body" />
                            <FontPicker label="Heading font" value={headingFont} onChange={setHeadingFont} testid="font-heading" />
                        </div>
                    </section>

                    <button data-testid="theme-save" disabled={busy} onClick={save} className="bg-primary text-primary-foreground rounded-xl px-6 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60">
                        {busy && <Loader2 size={16} className="animate-spin" />} Save theme
                    </button>
                </div>

                <ThemePreviewCard preset={preset} bodyFont={bodyFont} headingFont={headingFont} previewTokens={previewTokens} />
            </div>
        </div>
    );
}

function FontPicker({ label, value, onChange, testid }) {
    return (
        <label className="block bg-card border border-border rounded-2xl p-4">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="block w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1.5 font-semibold">
                {FONT_CHOICES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <p
                className="text-2xl mt-3 leading-tight font-bold"
                style={{ fontFamily: `"${value}", system-ui, sans-serif` }}
            >Aa Bb Cc 1234</p>
        </label>
    );
}

function ThemePreviewCard({ preset, bodyFont, headingFont, previewTokens }) {
    if (!preset) return null;
    const tokens = previewTokens || preset.tokens;
    return (
        <div className="md:sticky md:top-4 h-fit">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Live preview</h2>
            <div
                data-testid="theme-preview-card"
                className="rounded-2xl border border-border shadow-sm overflow-hidden"
                style={{ background: `hsl(${tokens["--background"]})`, color: `hsl(${tokens["--foreground"]})` }}
            >
                <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: `hsl(${tokens["--muted-foreground"]})` }}>DELIVER TO</p>
                    <p className="font-bold mt-0.5" style={{ fontFamily: `"${headingFont}", sans-serif` }}>Madhapur, Hyderabad</p>
                    <div className="mt-4 rounded-xl px-3 py-2.5 text-sm" style={{ background: `hsl(${tokens["--muted"]})`, color: `hsl(${tokens["--muted-foreground"]})` }}>
                        <span style={{ fontFamily: `"${bodyFont}", sans-serif` }}>Search dishes & restaurants</span>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2.5">
                        {["FOOD DELIVERY", "STORE COUPONS"].map((t) => (
                            <div key={t} className="rounded-2xl p-3" style={{ background: `hsl(${tokens["--card"]})`, color: `hsl(${tokens["--card-foreground"]})`, border: `1px solid hsl(${tokens["--border"]})` }}>
                                <p className="text-[11px] font-extrabold uppercase tracking-wider" style={{ fontFamily: `"${headingFont}", sans-serif` }}>{t}</p>
                                <span className="inline-block mt-3 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: `hsl(${tokens["--primary"]})` }}>Online</span>
                            </div>
                        ))}
                    </div>
                    <button className="mt-4 w-full rounded-xl py-2.5 text-sm font-bold text-white" style={{ background: `hsl(${tokens["--primary"]})`, fontFamily: `"${headingFont}", sans-serif` }}>
                        Place Order — ₹385
                    </button>
                </div>
            </div>
        </div>
    );
}
